import { useState, useEffect, useRef } from "react";
import useGetApi from "../../util/useGetApi";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import CheckEmail from "./CheckEmail";
import { useToast } from "../../contexts/ToastContext";
import Captcha from "../Captcha";

const Login = () => {
  let initialState = {
    firstName: {
      value: "",
      error: false,
    },
    lastName: {
      value: "",
      error: false,
    },
    dob: {
      value: "",
      error: false,
    },
    lastFourSocial: {
      value: "",
      error: false,
    },
    email: {
      value: "",
      error: false,
    },
  };

  const [form, setForm] = useState(initialState);
  const [formEnabled, setFormEnabled] = useState(true);
  const [formError, setFormError] = useState("");
  const [showCheckEmail, setShowCheckEmail] = useState(false);
  const [emailSent, setEmailSent] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  const [searchParams] = useSearchParams();
  const { callLogin, loading } = useGetApi();
  const { showError } = useToast();
  const captchaRef = useRef(null);

  const RECAPTCHA_SITE_KEY = window.RECAPTCHA_SITE_KEY;

  // The backend redeems magic links and 302s back here; on failure the
  // redirect appends ?login_error=1 so we can surface a toast.
  useEffect(() => {
    if (searchParams.get("login_error")) {
      showError("Your login link has expired. Please log in again.", 6000);
    }
  }, [searchParams, showError]);

  // Handle expired token from URL
  useEffect(() => {
    const unauthorized = searchParams.get("unauthorized");
    if (unauthorized) {
      showError("Your login has expired. Please log in again.", 6000);
    }
  }, [searchParams, showError]);

  // Captcha handlers
  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setCaptchaError("");
  };

  const handleCaptchaExpired = () => {
    setCaptchaValue("");
    setCaptchaError("Captcha expired. Please verify again.");
  };

  const handleCaptchaError = () => {
    setCaptchaValue("");
    setCaptchaError("Captcha verification failed. Please try again.");
  };

  const resetCaptcha = () => {
    if (captchaRef.current) {
      captchaRef.current.reset();
    }
    setCaptchaValue("");
  };

  function validateField(input, value) {
    let error = false;
    if (value === "") error = true;
    else if (input === "email") {
      const regexExp =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;

      if (regexExp.test(value)) error = false;
      else error = true;
    } else if (input === "lastFourSocial") {
      if ((!isNaN(value) && value.length === 4) === false) {
        error = true;
      }
    } else if (input === "dob") {
      if (value) {
        const dobArray = value.split("-");
        if (dobArray[0] < 1890 || dobArray[0] > 2020) error = true;
        else if (dobArray[1] < 1 || dobArray[1] > 12) error = true;
        else if (dobArray[2] < 1 || dobArray[2] > 31) error = true;
      } else error = true;
    }
    return error;
  }

  function updateForm(input, value) {
    let error = false;

    error = validateField(input, value);

    let tFormEnabled = true;
    if (error === false) {
      Object.entries(form).forEach(([key, value]) => {
        // Skip over the key we're currently updating as it isn't erroring
        // and the value in the form is stale
        if (key !== input && value.error === true) {
          tFormEnabled = false;
        }
      });
    } else tFormEnabled = false;

    setFormEnabled(tFormEnabled);

    setForm({
      ...form,
      [input]: {
        value,
        error,
      },
    });
  }

  async function handleFormSubmission(e) {
    e.preventDefault();

    //Check form fields first
    let error = false;
    let canContinue = true;
    let currentForm = {
      ...form,
    };
    Object.entries(form).forEach(([key, value]) => {
      if (validateField(key, value.value)) {
        error = true;
        canContinue = false;
        currentForm = {
          ...currentForm,
          [key]: {
            value: value.value,
            error,
          },
        };
      }
    });
    setForm({
      ...currentForm,
    });

    // Check captcha validation - REQUIRED
    if (!captchaValue) {
      setCaptchaError("Please complete the captcha verification.");
      return;
    }

    if (canContinue) {
      // Show a message to indicate we're logging in
      setFormError("Logging in... Please wait.");
      setCaptchaError("");

      console.log("Login form submitted with:", {
        firstName: form.firstName.value,
        lastName: form.lastName.value,
        dob: form.dob.value,
        lastFourSocial: form.lastFourSocial.value,
        email: form.email.value,
      });

      const result = await callLogin(
        {
          firstName: form.firstName.value,
          lastName: form.lastName.value,
          dob: form.dob.value,
          lastFourSocial: form.lastFourSocial.value,
          email: form.email.value,
        },
        captchaValue,
      );

      if (result && result.magicLinkSent) {
        setEmailSent(result.email);
        setShowCheckEmail(true);
        setFormError("");
        resetCaptcha();
      } else {
        setFormError("Login failed. Please try again.");
        setFormEnabled(true);
        resetCaptcha();
      }
    } else {
      setFormError("Please fill out all fields correctly.");
    }
  }

  function renderSpinner() {
    return (
      <div className="flex justify-center w-full" role="status">
        <svg
          aria-hidden="true"
          className="w-6 h-6 text-white animate-spin fill-as-green-50 dark:text-gray-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  // Show check email screen if magic link was sent
  if (showCheckEmail) {
    return <CheckEmail email={emailSent} />;
  }

  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <section className="grid h-full grid-cols-1 gap-0 md:grid-cols-2">
        <div className="flex flex-col px-6 py-16 bg-gray-50 md:px-10 md:py-10 lg:px-16 lg:py-16">
          <h1 className="text-base font-bold text-as-green-100 md:text-lg">
            Login / Sign Up
          </h1>
          <h2 className="mt-2 mb-4 text-3xl md:mb-5 md:mt-3 md:text-4xl">
            Let’s get started!
          </h2>
          <p>
            First, we need to verify your identity with your retirement account
            provider.
          </p>
          <p className="mt-8 text-xs md:mt-auto">
            We use Bank-level, 256-bit encryption to keep your data safe.
          </p>
        </div>
        <form className="flex flex-col px-6 pt-16 pb-16 space-y-5 bg-white md:px-10 md:pb-10 md:pt-20 lg:px-16 lg:pb-14 lg:pt-28">
          <div>
            <label
              className="block mb-1 text-sm font-bold text-gray-800"
              htmlFor="firstName"
            >
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={form.firstName.value}
              onChange={(e) => updateForm("firstName", e.target.value)}
              required
              autoComplete="given-name"
              aria-label="First Name"
              className={`mb-0 w-full rounded border-0 px-3 py-2 text-base text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50 ${
                form.firstName.error
                  ? "!ring-red-300 placeholder:text-red-300 focus:ring-inset focus:!ring-red-500"
                  : ""
              }`}
              {...(form.firstName.error && {
                "aria-invalid": "true",
                "aria-errormessage": "first-name-error",
              })}
            />
          </div>

          <div>
            <label
              className="block mb-1 text-sm font-bold text-gray-800"
              htmlFor="lastName"
            >
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={form.lastName.value}
              onChange={(e) => updateForm("lastName", e.target.value)}
              required
              autoComplete="family-name"
              aria-label="Last Name"
              className={`mb-0 w-full rounded border-0 px-3 py-2 text-base text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50 ${
                form.lastName.error
                  ? "!ring-red-300 placeholder:text-red-300 focus:ring-inset focus:!ring-red-500"
                  : ""
              }`}
              {...(form.lastName.error && {
                "aria-invalid": "true",
                "aria-errormessage": "last-name-error",
              })}
            />
          </div>

          <div>
            <label
              className="block mb-1 text-sm font-bold text-gray-800"
              htmlFor="dob"
            >
              Date of Birth (mm/dd/yyyy)
            </label>
            <input
              type="date"
              id="dob"
              name="dob"
              value={form.dob.value}
              onChange={(e) => updateForm("dob", e.target.value)}
              required
              autoComplete="bday"
              aria-label="Date of Birth"
              className={`mb-0 w-full rounded border-0 px-3 py-2 text-base text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50 ${
                form.dob.error
                  ? "!ring-red-300 placeholder:text-red-300 focus:ring-inset focus:!ring-red-500"
                  : ""
              }`}
              {...(form.dob.error && {
                "aria-invalid": "true",
                "aria-errormessage": "date-of-birth-error",
              })}
            />
          </div>

          <div>
            <label
              className="block mb-1 text-sm font-bold text-gray-800"
              htmlFor="lastFourSocial"
            >
              Last 4 Digits of SSN
            </label>
            <input
              type="tel"
              id="lastFourSocial"
              name="lastFourSocial"
              value={form.lastFourSocial.value}
              onChange={(e) => updateForm("lastFourSocial", e.target.value)}
              pattern="[0-9]{4}"
              maxLength="4"
              required
              autoComplete="off"
              aria-label="Last 4 Digits of SSN"
              className={`mb-0 w-full rounded border-0 px-3 py-2 text-base text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50 ${
                form.lastFourSocial.error
                  ? "!ring-red-300 placeholder:text-red-300 focus:ring-inset focus:!ring-red-500"
                  : ""
              }`}
              {...(form.lastFourSocial.error && {
                "aria-invalid": "true",
                "aria-errormessage": "ssn-error",
              })}
            />
          </div>

          <div className="!mb-10">
            <label
              className="block mb-1 text-sm font-bold text-gray-800"
              htmlFor="email"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={form.email.value}
              onChange={(e) => updateForm("email", e.target.value)}
              required
              autoComplete="email"
              aria-label="Email"
              className={`mb-0 w-full rounded border-0 px-3 py-2 text-base text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50 ${
                form.email.error
                  ? "!ring-red-300 placeholder:text-red-300 focus:ring-inset focus:!ring-red-500"
                  : ""
              }`}
            />
          </div>

          {/* Captcha Component */}
          <div className="!mb-6">
            <Captcha
              ref={captchaRef}
              siteKey={RECAPTCHA_SITE_KEY}
              onChange={handleCaptchaChange}
              onExpired={handleCaptchaExpired}
              onError={handleCaptchaError}
              theme="light"
              size="normal"
            />
            {captchaError && (
              <p className="text-sm text-red-600 mt-2">{captchaError}</p>
            )}
          </div>

          <p className="text-sm" hidden={!formError}>
            {formError}
          </p>
          <div className="!mt-0 flex gap-3 md:!mt-auto md:justify-end md:gap-5">
            <button
              className={`btn btn--primary h-12 min-w-[7rem] ${loading ? "bg-as-green-100" : ""} ${!captchaValue ? "opacity-50 cursor-not-allowed" : ""}`}
              type="submit"
              disabled={!formEnabled || !captchaValue}
              onClick={handleFormSubmission}
            >
              {loading ? renderSpinner() : <span>Submit</span>}
            </button>
          </div>
        </form>
      </section>
    </motion.div>
  );
};

export default Login;
